/**
 * Several roles per person (ALN-121, docs/pages/roles.md): rank order and the words for a set of roles,
 * shared by Administration, the account menu and the audit log. The server decides access; these only
 * say what it decided.
 */
export const ROLE_RANK = ["owner", "controller", "winemaker", "cellar", "viewer", "estimator"] as const;
export type RoleId = (typeof ROLE_RANK)[number];

const LABEL: Record<RoleId, string> = { owner: "Owner", controller: "Controller", winemaker: "Winemaker", cellar: "Cellar", viewer: "Viewer", estimator: "Estimator" };
export const roleLabel = (r: string) => LABEL[r as RoleId] ?? r.charAt(0).toUpperCase() + r.slice(1);

/** Each role's line, from the Roles table in the help (roles.md F.1). */
export const ROLE_LINE: Record<RoleId, string> = {
  owner: "Everything, including who has which role and making other owners. There's always at least one.",
  controller: "Everything an owner can except changing owners: rates, the monthly close, QuickBooks and users.",
  winemaker: "Plan programs and the process map and approve them, change rates, see the monthly close (not approve it), send surveys and see results and costs.",
  cellar: "Send and answer surveys, see costs and inventory.",
  estimator: "Answer the surveys sent to them. No costs.",
  viewer: "See costs and inventory, read-only.",
};

/** The roles, without repeats, in rank order. */
export const rankRoles = (rs: readonly string[] | null | undefined): RoleId[] => ROLE_RANK.filter(r => (rs ?? []).includes(r));

/** "Winemaker, Cellar" (the Roles column). */
export const rolesList = (rs: readonly string[]) => rankRoles(rs).map(roleLabel).join(", ");

/** "Winemaker and Cellar" (sentences, the account menu, the audit log). */
export function rolesAnd(rs: readonly string[]): string {
  const a = rankRoles(rs).map(roleLabel);
  return a.length < 2 ? (a[0] ?? "") : `${a.slice(0, -1).join(", ")} and ${a[a.length - 1]}`;
}

/** A person's roles from an API record: `roles`, or the older single `role`. */
export const rolesOf = (u: { role?: string | null; roles?: readonly string[] | null }): RoleId[] => rankRoles(u.roles?.length ? u.roles : u.role ? [u.role] : []);

/** The roles two-step covers under a winery's setting (sign-in-methods.md C). */
export function twoStepRolesOf(s: { twoStepScope: string; twoStepRoles: string[] } | undefined): string[] {
  if (!s) return [];
  if (s.twoStepScope === "everyone") return [...ROLE_RANK];
  if (s.twoStepScope === "owners_controllers") return ["owner", "controller"];
  if (s.twoStepScope === "roles") return s.twoStepRoles;
  return [];
}
